
import { useEffect, useState } from "react"
import {
  Users,
  FileText,
  DollarSign,
  BookOpen,
  Shield,
  Share2,
  Bell,
  Target,
} from "lucide-react"
import { Layout } from "@/shared/components"
import {
  ClientEnrollmentWizard,
  ClientSelfEnrollmentWizard,
  AgentSalesReports,
  TrainingDashboard,
  TrainingGate,
  AgentComplianceMaterials,
  AgentShareInstructions,
  AgentPendingApplications,
  AgentCampaignSelector,
  useAgentStore
} from "@/features/agents"
import { useCampaign, useCampaignStore } from "@/features/campaigns"


export default function AgentDashboard() {
  // ✅ Zustand stores
  const { agent, fetchAgentData } = useAgentStore();
  const { 
    selectedCampaign, 
    availableCampaigns, 
    setSelectedCampaign,
    fetchCampaigns,
    isLoading: campaignsLoading 
  } = useCampaignStore();

  // Keep using CampaignContext for backward compatibility
  const { selectedCampaign: contextCampaign, selectCampaign: contextSelectCampaign, campaigns: contextCampaigns } = useCampaign();

  // Initialize stores on mount
  useEffect(() => {
    fetchAgentData();
    fetchCampaigns();
  }, [fetchAgentData, fetchCampaigns]);

  // Get initial tab from URL hash or default to campaign selection
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '')
    const validTabs = ["campaign-selection", "enrollment", "self-enrollment", "sales-reports", "training", "compliance", "sharing", "pending-applications"]
    return validTabs.includes(hash) ? hash : "campaign-selection"
  }

  const [activeTab, setActiveTab] = useState(getInitialTab)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  const [pendingViewKey, setPendingViewKey] = useState<number>(0)
  const [resumeRequested, setResumeRequested] = useState<boolean>(false)
  const [intendedDestination, setIntendedDestination] = useState<string | null>(null)

  // Handle direct URL access - redirect to campaign selection if no campaign is selected
  useEffect(() => {
    const currentTab = activeTab
    const requiresCampaign = ['enrollment', 'self-enrollment', 'pending-applications'].includes(currentTab)
    
    if (requiresCampaign && !selectedCampaign) {
      setIntendedDestination(currentTab)
      handleTabChange('campaign-selection')
    }
  }, [activeTab, selectedCampaign])

  // Redirect to intended destination after campaign selection
  useEffect(() => {
    if (selectedCampaign && intendedDestination) {
      handleTabChange(intendedDestination)
      setIntendedDestination(null)
    }
  }, [selectedCampaign, intendedDestination])

  // Handle URL hash changes (browser back/forward or direct URL access)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '')
      const validTabs = ["campaign-selection", "enrollment", "self-enrollment", "sales-reports", "training", "compliance", "sharing", "pending-applications"]
      
      if (validTabs.includes(hash)) {
        const requiresCampaign = ['enrollment', 'self-enrollment', 'pending-applications'].includes(hash)
        
        if (requiresCampaign && !selectedCampaign) {
          setIntendedDestination(hash)
          setActiveTab('campaign-selection')
          window.history.replaceState(null, '', '#campaign-selection')
        } else {
          setActiveTab(hash)
        }
      }
    }

    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [selectedCampaign])


  // Listen for pending updates fired from other views (e.g., New Enrollment action)
  useEffect(() => {
    const onPendingUpdated = () => {
      if (activeTab === 'pending-applications') {
        setPendingViewKey(Date.now())
      }
    }
    window.addEventListener('pending:updated', onPendingUpdated as any)
    return () => window.removeEventListener('pending:updated', onPendingUpdated as any)
  }, [activeTab])

  useEffect(() => {
    const onNavigateEnrollment = () => {
      setResumeRequested(true)
      handleTabChange('enrollment')
      setMobileSidebarOpen(false)
    }
    
    const onNavigateEnrollmentFollowup = () => {
      setResumeRequested(true)
      handleTabChange('enrollment')
      setMobileSidebarOpen(false)
    }
    
    const onNavigateTraining = () => {
      handleTabChange('training')
      setMobileSidebarOpen(false)
    }
    
    const onResetResumeFlag = () => {
      setResumeRequested(false)
    }

    window.addEventListener('navigate:enrollment', onNavigateEnrollment as any)
    window.addEventListener('navigate:enrollment-followup', onNavigateEnrollmentFollowup as any)
    window.addEventListener('navigate:training', onNavigateTraining as any)
    window.addEventListener('reset-resume-flag', onResetResumeFlag as any)
    return () => {
      window.removeEventListener('navigate:enrollment', onNavigateEnrollment as any)
      window.removeEventListener('navigate:enrollment-followup', onNavigateEnrollmentFollowup as any)
      window.removeEventListener('navigate:training', onNavigateTraining as any)
      window.removeEventListener('reset-resume-flag', onResetResumeFlag as any)
    }
  }, [])

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    // Update URL hash without triggering page reload
    window.history.replaceState(null, '', `#${tabId}`)
  }

  const navigationItems = [
    { id: "campaign-selection", label: "Campaign Selection", icon: Target },
    { id: "enrollment", label: "Client Enrollment", icon: Users },
    { id: "self-enrollment", label: "Self Enrollment", icon: FileText },
    { id: "sales-reports", label: "Sales Reports", icon: DollarSign },
    // { id: "pay-reports", label: "Pay Reports", icon: Clock },
    { id: "training", label: "Training", icon: BookOpen },
    { id: "compliance", label: "Compliance", icon: Shield },
    { id: "sharing", label: "Share Tools", icon: Share2 },
    { id: "pending-applications", label: "Applications", icon: Bell },
  ]

  const renderContent = () => {
    switch (activeTab) {
      case "campaign-selection":
        return (
          <AgentCampaignSelector 
            campaigns={availableCampaigns.length > 0 ? availableCampaigns : contextCampaigns}
            loading={campaignsLoading}
            selectedCampaignId={selectedCampaign?.id || contextCampaign?.id}
            onCampaignSelect={(campaign) => {
              // Update both Zustand store and Context for compatibility
              setSelectedCampaign(campaign)
              contextSelectCampaign(campaign)
              // If we have an intended destination, redirect there after campaign selection
              if (intendedDestination) {
                handleTabChange(intendedDestination)
                setIntendedDestination(null)
              }
            }} 
          />
        )
      case "enrollment":
        return (
          <TrainingGate moduleType="ENROLLMENT">
            <ClientEnrollmentWizard 
              selectedCampaign={selectedCampaign || contextCampaign} 
              onCampaignSelect={(campaign) => {
                setSelectedCampaign(campaign)
                contextSelectCampaign(campaign)
              }} 
            />
          </TrainingGate>
        )
      case "self-enrollment":
        return (
          <TrainingGate moduleType="SELF_ENROLLMENT">
            <ClientSelfEnrollmentWizard selectedCampaign={selectedCampaign || contextCampaign} />
          </TrainingGate>
        )
      case "sales-reports":
        return (
          <TrainingGate moduleType="SALES_REPORTS">
            <AgentSalesReports />
          </TrainingGate>
        )
      case "training":
        return <TrainingDashboard />
      case "compliance":
        return (
          <TrainingGate moduleType="COMPLIANCE">
            <AgentComplianceMaterials />
          </TrainingGate>
        )
      case "sharing":
        return <AgentShareInstructions />
      case "pending-applications":
        return <AgentPendingApplications key={pendingViewKey} selectedCampaign={selectedCampaign} />
      default:
        return (
          <div className="card">
            <h2 className="text-xl font-semibold text-accent-900 mb-4">{activeTab.replace("-", " ").toUpperCase()}</h2>
            <div className="bg-accent-50 p-4 rounded-lg">
              <p className="text-sm text-accent-700">
                {activeTab.replace("-", " ")} functionality will be implemented here.
              </p>
            </div>
          </div>
        )
    }
  }

  return (
    <Layout title="Agent Dashboard" onMenuToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}>
      {/* Overlay for mobile sidebar */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-0 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      <div className="flex flex-1">
        {/* Sidebar Navigation - Fixed and Responsive */}
        <div
          className={`fixed top-0 sm:top-16 left-0 w-72 h-screen sm:h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-slate-50 to-slate-100 border-r border-slate-300 p-6 z-10
          ${mobileSidebarOpen ? "block" : "hidden"} lg:block`}
        >
          {/* Mobile header space */}
          <div className="h-14 sm:hidden"></div>
          
          {/* Campaign Status */}
          {selectedCampaign && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Selected Campaign</h3>
              <div className="p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-800">{selectedCampaign.name}</p>
                    <p className="text-xs text-emerald-600">Campaign Active</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedCampaign(null)
                      contextSelectCampaign(null)
                      handleTabChange('campaign-selection')
                    }}
                    className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <nav className="space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon
              const isActive = activeTab === item.id
              
              // Enable/disable based on campaign selection
              // Campaign selection tab is always enabled
              // All other tabs are disabled until a campaign is selected
              const isDisabled = item.id !== 'campaign-selection' && !selectedCampaign
              
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (isDisabled) {
                      // If trying to access a disabled tab, redirect to campaign selection
                      handleTabChange('campaign-selection')
                      setMobileSidebarOpen(false)
                      return
                    }
                    
                    if (item.id === 'enrollment') {
                      if (!resumeRequested) {
                        try { localStorage.removeItem('enrollmentSessionId') } catch {}
                      }
                      // Don't reset resumeRequested immediately - let the enrollment component handle it
                    }
                    handleTabChange(item.id)
                    if (item.id === 'pending-applications') {
                      setPendingViewKey(Date.now())
                    }
                    setMobileSidebarOpen(false)
                  }}
                  disabled={isDisabled}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl transition-all duration-200 ${
                    isDisabled
                      ? "bg-slate-50 text-slate-400 cursor-not-allowed opacity-60"
                      : isActive 
                        ? "bg-blue-600 text-white shadow-md" 
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                  data-nav-id={item.id}
                >
                  <div className="relative">
                    <Icon className={`w-5 h-5 flex-shrink-0 ${
                      isActive ? "text-white" : isDisabled ? "text-slate-400" : "text-slate-500"
                    }`} />
                  </div>
                  <span className="font-medium text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                    {item.label}
                  </span>
                </button>
              )
            })}
          </nav>
        </div>
        {/* Main Content Area - Scrolls independently and adjusts for sidebar */}
        <div className="flex-1 lg:ml-72 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-14 sm:pt-16">{renderContent()}</div>
      </div>
    </Layout>
  )
}
